/**
 * WP4 — CompanionLayer: kotek-strażnik zawsze obok postaci (piesek/smok).
 * Overlay w SceneRouter; podąża za follow-target lub siedzi w rogu w mini-grach.
 */
import Phaser from 'phaser';
import { BaseScene } from '../core/BaseScene.js';
import { COMPANION_INTENTS, GAME_HEIGHT, GAME_WIDTH } from '@pieskowo/shared';
import { CompanionFx, type CompanionFxHandles } from './CompanionFx.js';
import { StuckDetector, type StuckHint } from './StuckDetector.js';
import { AskDialog } from './AskDialog.js';
import {
  FirstInteractionTracker,
  type WorldEventLike,
} from './worldEvents.js';
import {
  createCompanionFollowState,
  stepCompanionFollow,
  type CompanionFollowState,
} from './followMotion.js';
import {
  catPoseTextureKey,
  isUsableCompanionTexture,
  pickCompanionSourceUrl,
} from './companionAsset.js';

/** Szyna z scen (Cafe/School/…) — ObjectLayer + mission hooks. */
export const INTERACT_BUS_EVENT = 'pieskowo:interact';

const OVERLAY_KEYS = new Set([
  'Hud',
  'CompanionLayer',
  'QuestLayer',
  'PhoneOverlay',
  'Reward',
  'Boot',
  'Preload',
  'Splash',
]);

const MINIGAME_PREFIX = 'Mg';

/** Offset za ogonem (ekranowy). */
const FOLLOW_OFFSET = { x: -56, y: -28 };
const CORNER = { x: GAME_WIDTH - 90, y: GAME_HEIGHT - 90 };

export type PlayerForm = 'dog' | 'dragon';

export type CompanionLayerData = {
  /** Pozycja follow (świat/scena gry). */
  follow?: { x: number; y: number };
  form?: PlayerForm;
};

export class CompanionLayerScene extends BaseScene {
  static readonly KEY = 'CompanionLayer';

  private cat!: Phaser.GameObjects.Container;
  private catSprite!: Phaser.GameObjects.Image | Phaser.GameObjects.Arc;
  private stuck = new StuckDetector();
  private dialog: AskDialog | null = null;
  private bubble: Phaser.GameObjects.Container | null = null;
  private fxHandle: CompanionFxHandles | null = null;
  private targetX = CORNER.x;
  private targetY = CORNER.y;
  private form: PlayerForm = 'dog';
  private appearPlayed = false;
  private hintText: Phaser.GameObjects.Text | null = null;
  private tickAcc = 0;
  private firstIx = new FirstInteractionTracker();
  private busHandler: ((payload: unknown) => void) | null = null;
  /** Do kiedy nie nadpisywać targetX/Y (kotek „leci" wskazać hotspot/atrakcję). */
  private distractUntil = 0;
  private catAnimAcc = 0;
  private catWalkFrame = false;
  private followMotion: CompanionFollowState = createCompanionFollowState(
    CORNER.x,
    CORNER.y,
  );

  constructor() {
    super(CompanionLayerScene.KEY);
  }

  preload(): void {
    for (const pose of ['idle', 'walkA', 'walkB'] as const) {
      const key = `cat_${pose}`;
      if (!this.textures.exists(key)) {
        this.load.image(key, `/assets/real/${key}.png`);
      }
    }
  }

  create(): void {
    this.cameras.main.setBackgroundColor('rgba(0,0,0,0)');
    this.cat = this.buildCat();
    this.cat.setPosition(CORNER.x, CORNER.y);
    this.followMotion = createCompanionFollowState(CORNER.x, CORNER.y);
    this.cat.setDepth(4000);
    this.cat.setScrollFactor(0);

    if (!this.appearPlayed) {
      this.fxHandle = CompanionFx.playAppear(this, this.cat);
      this.appearPlayed = true;
    }

    // Global empty-tap detector (nie nachalny — StuckDetector ma cooldown)
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (this.dialog) return;
      const hit = this.input.hitTestPointer(pointer);
      const onlyCat =
        hit.length > 0 && hit.every((o) => this.isCatObject(o));
      if (hit.length === 0 || onlyCat) {
        if (!onlyCat) {
          const hint = this.stuck.recordEmptyTap();
          if (hint) this.onStuckHint(hint);
        }
      } else {
        this.stuck.recordAction();
      }
    });

    // Subskrypcja interact z cafe/school/… (PLAN-V2 §3.3)
    this.busHandler = (payload: unknown) => {
      if (payload && typeof payload === 'object') {
        this.onWorldEvent(payload as WorldEventLike);
      }
    };
    this.game.events.on(INTERACT_BUS_EVENT, this.busHandler);

    this.events.on('shutdown', () => this.cleanup());
    this.events.on('destroy', () => this.cleanup());
  }

  /**
   * Hook V2: kotek komentuje pierwszą interakcję danego typu w sesji
   * (ikona-dymek z companionIntents FIRST_INTERACTION_*).
   * Wywoływane z szyny pieskowo:interact lub ręcznie.
   */
  onWorldEvent(evt: WorldEventLike): void {
    this.stuck.recordAction();
    const intent = this.firstIx.observe(evt);
    if (!intent) return;

    const meta = COMPANION_INTENTS[intent];
    this.fxHandle?.stop();
    this.fxHandle = CompanionFx.playHelp(this, this.cat, 1800);
    this.showHintLabel(meta.labelPl);
    // dymek z ikoną (label PL, lokalny — bez round-tripu do serwera); responseId = intent na E2E
    this.showResponseBubble(meta.labelPl, intent);
  }

  private isCatObject(obj: Phaser.GameObjects.GameObject): boolean {
    let p: Phaser.GameObjects.GameObject | null = obj;
    while (p) {
      if (p === this.cat) return true;
      p = (p as Phaser.GameObjects.GameObject & { parentContainer?: Phaser.GameObjects.Container })
        .parentContainer as unknown as Phaser.GameObjects.GameObject | null;
    }
    return false;
  }

  private buildCat(): Phaser.GameObjects.Container {
    const parts: Phaser.GameObjects.GameObject[] = [];

    const textureExists = this.textures.exists('cat_idle');
    const decodedSource = textureExists
      ? (this.textures.get('cat_idle').getSourceImage() as
          | { currentSrc?: string; src?: string }
          | undefined)
      : undefined;
    const manifestSource = this.registry.get('assetSource:cat_idle') as
      | string
      | undefined;
    // Prefer decoded texture URL — registry assetSource must not veto real art.
    const useTex = isUsableCompanionTexture(
      textureExists,
      pickCompanionSourceUrl(
        decodedSource?.currentSrc || decodedSource?.src,
        manifestSource,
      ),
    );
    if (useTex) {
      const img = this.add.image(0, 0, 'cat_idle').setDisplaySize(72, 72);
      this.catSprite = img;
      parts.push(img);
    } else {
      // Proceduralny maneki-neko (placeholder gdy brak atlasu WP13)
      const body = this.add.circle(0, 4, 28, 0xfff8f0).setStrokeStyle(3, 0xe8d5c4);
      const earL = this.add.triangle(-16, -22, 0, 18, 14, 18, 7, 0, 0xfff8f0);
      const earR = this.add.triangle(16, -22, 0, 18, 14, 18, 7, 0, 0xfff8f0);
      const collar = this.add.rectangle(0, 18, 36, 8, 0xe74c3c);
      const bell = this.add.circle(0, 24, 5, 0xffd700);
      const eyeL = this.add.circle(-10, 0, 3, 0x4a3f55);
      const eyeR = this.add.circle(10, 0, 3, 0x4a3f55);
      this.catSprite = body;
      parts.push(earL, earR, body, collar, bell, eyeL, eyeR);
    }

    const hit = this.add
      .circle(0, 0, 40, 0xffffff, 0.001)
      .setInteractive({ useHandCursor: true });
    hit.on('pointerdown', (p: Phaser.Input.Pointer) => {
      p.event?.stopPropagation?.();
      this.openAskDialog();
    });
    parts.push(hit);

    const c = this.add.container(0, 0, parts);
    c.setSize(80, 80);
    return c;
  }

  private activeGameSceneKey(): string {
    const scenes = this.game.scene.getScenes(true);
    for (const s of scenes) {
      const k = s.scene.key;
      if (!OVERLAY_KEYS.has(k)) return k;
    }
    return 'Town';
  }

  private isMinigame(key: string): boolean {
    return key.startsWith(MINIGAME_PREFIX);
  }

  /**
   * API dla awatara / net: aktualizuj pozycję follow + formę piesek|smok.
   */
  setFollow(pos: { x: number; y: number } | null, form?: PlayerForm): void {
    if (form) this.form = form;
    if (!pos) return;
    // companionFollow jest w WSPÓŁRZĘDNYCH ŚWIATA — kotek ma scrollFactor 0
    // (overlay), więc mapujemy na ekran kamery aktywnej sceny gry.
    const oy = this.form === 'dragon' ? FOLLOW_OFFSET.y - 20 : FOLLOW_OFFSET.y;
    const gameKey = this.activeGameSceneKey();
    const gameScene = this.game.scene.getScene(gameKey) as Phaser.Scene | null;
    const cam = gameScene?.cameras?.main;
    // Świat → ekran (overlay ma scrollFactor 0): odejmij scroll kamery.
    let sx = pos.x;
    let sy = pos.y;
    if (cam) {
      sx = (pos.x - cam.scrollX) * cam.zoom;
      sy = (pos.y - cam.scrollY) * cam.zoom;
    }
    this.targetX = Phaser.Math.Clamp(sx + FOLLOW_OFFSET.x, 40, GAME_WIDTH - 40);
    this.targetY = Phaser.Math.Clamp(sy + oy, 80, GAME_HEIGHT - 40);
  }

  setForm(form: PlayerForm): void {
    this.form = form;
  }

  /** Publiczne API stuck / denied z innych modułów. */
  getStuckDetector(): StuckDetector {
    return this.stuck;
  }

  notifyServerDenied(): void {
    const h = this.stuck.recordServerDenied();
    if (h) this.onStuckHint(h);
  }

  notifyLockedPlace(): void {
    const h = this.stuck.recordLockedPlace();
    if (h) this.onStuckHint(h);
  }

  notifyMinigameStart(): void {
    this.stuck.startMinigame();
  }

  notifyMinigameEnd(): void {
    this.stuck.endMinigame();
  }

  notifyMinigameStars(n: number): void {
    this.stuck.setMinigameStars(n);
  }

  private openAskDialog(): void {
    if (this.dialog) return;
    this.stuck.recordAction();
    const sceneKey = this.activeGameSceneKey();
    this.dialog = new AskDialog({
      scene: this,
      sceneKey,
      onClose: (result) => {
        this.dialog = null;
        if (result.responseId) {
          this.showResponseBubble(
            result.sentencePl ?? 'Kotek pomaga!',
            result.responseId,
          );
        }
      },
    });
  }

  private onStuckHint(hint: StuckHint): void {
    this.fxHandle?.stop();
    this.fxHandle = CompanionFx.playHelp(this, this.cat, 2000);
    const meta = COMPANION_INTENTS[hint.intentId];
    this.showHintLabel(meta.labelPl);

    if (hint.trigger === 'TAP_NOTHING') {
      // lot 2 s w stronę rogu (stub hotspot) — pełny hotspot = WP scen
      this.targetX = GAME_WIDTH * 0.35;
      this.targetY = GAME_HEIGHT * 0.55;
      this.distractUntil = this.time.now + 2000;
    }
    if (hint.trigger === 'IDLE_LONG' || hint.trigger === 'WORLD_IDLE') {
      // WORLD_IDLE (§4.3): kotek wskazuje okolicę atrakcji + machanie
      if (hint.trigger === 'WORLD_IDLE') {
        this.targetX = GAME_WIDTH * 0.45;
        this.targetY = GAME_HEIGHT * 0.5;
        this.distractUntil = this.time.now + 2000;
        this.showResponseBubble(meta.labelPl, 'WORLD_IDLE');
      }
      this.tweens.add({
        targets: this.cat,
        angle: { from: -8, to: 8 },
        duration: 200,
        yoyo: true,
        repeat: 3,
      });
    }
  }

  private showHintLabel(text: string): void {
    this.hintText?.destroy();
    this.hintText = this.add
      .text(this.cat.x, this.cat.y - 56, text, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#4a3f55',
        backgroundColor: '#fff8f0',
        padding: { x: 8, y: 4 },
      })
      .setOrigin(0.5)
      .setDepth(4100)
      .setScrollFactor(0);
    this.time.delayedCall(2500, () => {
      this.hintText?.destroy();
      this.hintText = null;
    });
  }

  /** Pokaż dymek z prawdziwym zdaniem PL (sentencePl z serwera lub lokalny fallback). */
  private showResponseBubble(sentencePl: string, responseId?: string): void {
    this.bubble?.destroy(true);
    const txt = this.add
      .text(0, 0, sentencePl, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '15px',
        color: '#4a3f55',
        align: 'center',
        wordWrap: { width: 220 },
      })
      .setOrigin(0.5);
    const bg = this.add
      .rectangle(0, 0, 260, Math.max(56, txt.height + 24), 0xfff8f0)
      .setStrokeStyle(3, 0xffb6c1);
    bg.setDepth(0);
    txt.setDepth(1);
    this.bubble = this.add.container(this.cat.x, this.cat.y - 70, [bg, txt]);
    this.bubble.setDepth(4200);
    this.bubble.setScrollFactor(0);
    // responseId zachowany w data na E2E
    this.bubble.setData('responseId', responseId);

    this.fxHandle?.stop();
    this.fxHandle = CompanionFx.playHelp(this, this.cat, 1800);

    this.time.delayedCall(3200, () => {
      this.bubble?.destroy(true);
      this.bubble = null;
    });
  }

  update(_t: number, dt: number): void {
    const key = this.activeGameSceneKey();
    if (this.time.now < this.distractUntil) {
      // kotek leci wskazać hotspot/atrakcję (TAP_NOTHING / WORLD_IDLE) —
      // nie nadpisuj celu dopóki dystrakcja trwa
    } else if (this.isMinigame(key)) {
      this.targetX = CORNER.x;
      this.targetY = CORNER.y;
    } else {
      // brak awatara (stuby) — kotek w rogu, lekko oddycha
      const follow = this.registry.get('companionFollow') as
        | { x: number; y: number; form?: PlayerForm }
        | undefined;
      if (follow) {
        this.setFollow(follow, follow.form);
      } else if (!this.isMinigame(key)) {
        // default corner for stubs
        this.targetX = CORNER.x;
        this.targetY = CORNER.y;
      }
    }

    this.followMotion = stepCompanionFollow(
      this.followMotion,
      { x: this.targetX, y: this.targetY },
      Math.min(0.05, Math.max(0, dt / 1000)),
    );
    const helpOffsetY = this.fxHandle?.offsetY?.() ?? 0;
    this.cat.setPosition(
      this.followMotion.x,
      this.followMotion.y + this.followMotion.bobOffsetY + helpOffsetY,
    );
    const dx = this.targetX - this.followMotion.x;
    const dy = this.targetY - this.followMotion.y;
    const walking = Math.hypot(dx, dy) > 3;
    this.updateCatPose(dt, walking, dx < 0);

    if (this.hintText) {
      this.hintText.setPosition(this.cat.x, this.cat.y - 56);
    }
    if (this.bubble) {
      this.bubble.setPosition(this.cat.x, this.cat.y - 70);
    }

    this.tickAcc += dt;
    if (this.tickAcc >= 500) {
      this.tickAcc = 0;
      const hint = this.stuck.tick();
      if (hint) this.onStuckHint(hint);
    }
  }

  private updateCatPose(
    dtMs: number,
    walking: boolean,
    facingLeft: boolean,
  ): void {
    if (!(this.catSprite instanceof Phaser.GameObjects.Image)) return;
    if (walking) {
      this.catAnimAcc += dtMs;
      if (this.catAnimAcc >= 160) {
        this.catAnimAcc = 0;
        this.catWalkFrame = !this.catWalkFrame;
      }
    } else {
      this.catAnimAcc = 0;
    }
    const key = catPoseTextureKey(walking, this.catWalkFrame);
    if (this.textures.exists(key) && this.catSprite.texture.key !== key) {
      this.catSprite.setTexture(key);
      this.catSprite.setDisplaySize(72, 72);
    }
    this.catSprite.setFlipX(facingLeft);
  }

  private cleanup(): void {
    if (this.busHandler) {
      this.game.events.off(INTERACT_BUS_EVENT, this.busHandler);
      this.busHandler = null;
    }
    this.fxHandle?.stop();
    this.fxHandle = null;
    this.dialog?.destroy();
    this.dialog = null;
    this.bubble?.destroy(true);
    this.bubble = null;
    this.hintText?.destroy();
    this.hintText = null;
  }
}

/**
 * Uruchom overlay kotka jeśli jeszcze nie aktywny.
 * Wywołanie z E2E / onboarding / scen gdy gra weszła w świat.
 */
export function ensureCompanionOverlay(game: Phaser.Game): void {
  const mgr = game.scene;
  if (mgr.isActive(CompanionLayerScene.KEY) || mgr.isPaused(CompanionLayerScene.KEY)) {
    return;
  }
  // Phaser ScenePlugin.launch — typ SceneManager w typings bywa bez launch
  (mgr as unknown as { run: (key: string) => void }).run(CompanionLayerScene.KEY);
}

export { CompanionFx } from './CompanionFx.js';
export { StuckDetector } from './StuckDetector.js';
export type { StuckHint, StuckTrigger } from './StuckDetector.js';
export { AskDialog } from './AskDialog.js';
export { askCompanion } from './askClient.js';
export {
  FirstInteractionTracker,
  firstInteractionIntentFromEvent,
  type FirstInteractionIntent,
  type WorldEventLike,
} from './worldEvents.js';
export {
  createCompanionFollowState,
  stepCompanionFollow,
  type CompanionFollowOptions,
  type CompanionFollowState,
} from './followMotion.js';

export const companionStub = { ready: true as const, module: 'companion' };
export default CompanionLayerScene;
